import { OrderStatus } from '../../models/orders';

export interface AutomationAction {
  message: string;
  subtitle: string;
  status: 'TODO' | 'IN_PROGRESS' | 'SUCCESS' | 'FAILURE';
  action:
    | 'createOrg'
    | 'updateOrgContact'
    | 'createFac'
    | 'updateFacContact'
    | 'createFacCC'
    | 'updateFacEcofit';
  entity?: { type: 'organization' | 'facility'; name: string; idx?: number; facilityId?: number };
}

export const generateActionsList: (ord: any) => AutomationAction[] = ord => {
  const list: AutomationAction[] = [];

  if (ord.organization.newOrExistingOrganization === 'new_organization') {
    list.push(
      {
        message: `Create new organization`,
        subtitle: ord.organization.name,
        status: 'TODO',
        action: 'createOrg',
        entity: { type: 'organization', name: ord.organization.name },
      },
      {
        message: `Update organization contact information`,
        subtitle: `${ord.productDetails.customerContactPhone ||
          ord.productDetails.customerContactName}/${ord.productDetails.customerContactEmail}`,
        status: 'TODO',
        action: 'updateOrgContact',
        entity: { type: 'organization', name: ord.organization.name },
      },
    );
  }

  ord.facility.facilities.forEach((fac, idx) => {
    if (fac.type === 'NEW') {
      list.push(
        {
          message: `Create new facility`,
          subtitle: fac.name,
          status: 'TODO',
          action: 'createFac',
          entity: { type: 'facility', name: fac.name, idx, facilityId: fac.facilityId || null },
        },
        {
          message: `Update Facility contact information`,
          subtitle: `${ord.productDetails.customerContactPhone ||
            ord.productDetails.customerContactName}/${ord.productDetails.customerContactEmail}`,
          status: 'TODO',
          action: 'updateFacContact',
          entity: { type: 'facility', name: fac.name, idx, facilityId: fac.facilityId || null },
        },
        {
          message: `Create new console-config`,
          subtitle: fac.name,
          status: 'TODO',
          action: 'createFacCC',
          entity: { type: 'facility', name: fac.name, idx, facilityId: fac.facilityId || null },
        },
      );
    } else {
      list.push({
        message: `Update Facility contact information`,
        subtitle: `${ord.productDetails.customerContactPhone ||
          ord.productDetails.customerContactName}/${ord.productDetails.customerContactEmail}`,
        status: 'TODO',
        action: 'updateFacContact',
        entity: { type: 'facility', name: fac.name, idx, facilityId: fac.facilityId },
      });
    }
  });

  if (ord.products?.productSelection?.FACILITY360) {
    Object.keys(ord.productDetails.FACILITY360).forEach((key, idx) => {
      if (key !== 'pricing') {
        list.push({
          message: 'Update facility Ecofit machine counts',
          subtitle: key,
          status: 'TODO',
          action: 'updateFacEcofit',
          entity: {
            type: 'facility',
            name: key,
            idx,
            facilityId: ord.facility.facilities[idx]?.facilityId,
          },
        });
      }
    });
  }

  //make status complete if already done before
  if (ord.orderActionsComplete > -1) {
    for (let i = 0; i < ord.orderActionsComplete + 1; i++) {
      list[i].status = 'SUCCESS';
    }
  }

  // A fulfilled order has completed every action; orderActionsComplete is not reliably
  // persisted on the success path, so mark all complete and surface the persisted IDs
  // that were generated during fulfillment.
  if (ord.orderStatus === OrderStatus.FULFILLED) {
    list.forEach(item => {
      item.status = 'SUCCESS';
      if (item.action === 'createOrg' && ord.organization.organizationId) {
        item.subtitle = `${item.subtitle} (Created Organization ID: ${ord.organization.organizationId})`;
      }
      if (item.action === 'createFac' && item.entity?.facilityId) {
        item.subtitle = `${item.subtitle} (Created Facility ID: ${item.entity.facilityId})`;
      }
    });
  }

  return list;
};

interface OrgFacMaps {
  organizations: {
    [name: string]: number;
  };
  facilities: {
    [name: string]: number;
  };
}

interface TransformInput {
  order: any;
  maps: OrgFacMaps;
  entity: { type: 'organization' | 'facility'; name: string; idx?: number; facilityId?: number };
}

export const actionOrderTransforms = {
  createOrg: ({ order }: TransformInput) => {
    return {
      name: order.organization.name,
    };
  },
  updateOrgContact: ({ order }: TransformInput) => {
    return {
      contact_email: order.productDetails.customerContactEmail,
      contact_phone_number:
        order.productDetails.customerContactPhone || order.productDetails.customerContactName,
    };
  },
  updateFacContact: ({ order }: TransformInput) => {
    return {
      contact_email: order.productDetails.customerContactEmail,
      contact_phone_number:
        order.productDetails.customerContactPhone || order.productDetails.customerContactName,
    };
  },
  createFac: ({ order, maps, entity }: TransformInput) => {
    return {
      name: order.facility.facilities[entity.idx].name,
      organization_id:
        maps.organizations[order.organization.name] ?? order.organization.organizationId,
      address: {
        street1: order.facility.facilities[entity.idx].street1 || '',
        street2: order.facility.facilities[entity.idx].street2 || '',
        city: order.facility.facilities[entity.idx].city,
        state_id: order.facility.facilities[entity.idx].stateId,
        country_id: order.facility.facilities[entity.idx].countryId,
        postal_code: order.facility.facilities[entity.idx].postalCode || '',
      },
    };
  },
  createFacCC: ({ maps, entity, order }: TransformInput) => {
    return {
      facility_id: maps.facilities[entity.name] ?? entity?.facilityId,
      ...(order?.products?.integrations?.YMCA360 && { integrations: { YMCA360: true } }),
    };
  },
  updateFacEcofit: ({ order, entity }: TransformInput) => {
    const res = {
      touchscreen_count: +order.productDetails.FACILITY360[entity.name].fac360TouchscreenCount || 0,
      led_count: +order.productDetails.FACILITY360[entity.name].fac360LEDCount || 0,
    } as any;

    if (order.productDetails.salesContactEmail) {
      res.sales_contact_email = order.productDetails.salesContactEmail;
    }
    return res;
  },
};
